import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, RefreshCw, BadgeCheck, Building, MapPin, Phone as PhoneIcon,
  Mail, TrendingUp, IndianRupee, ChevronRight, Stethoscope, Percent,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


function formatINR(n) {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}


export default function ReferringDoctorsList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/referring-doctors/');
        if (cancelled) return;
        setDoctors(resp?.results || resp?.data?.results || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load referring doctors');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const specialties = useMemo(() => {
    const set = new Set();
    for (const d of doctors) if (d.specialty) set.add(d.specialty);
    return Array.from(set).sort();
  }, [doctors]);

  const filtered = useMemo(() => {
    return doctors.filter(d => {
      if (search) {
        const q = search.trim().toLowerCase();
        const hay = [d.name, d.mobile, d.email, d.clinic_or_hospital, d.registration_number]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (specialtyFilter !== 'all' && d.specialty !== specialtyFilter) return false;
      if (verifiedFilter === 'verified'   && !d.is_verified) return false;
      if (verifiedFilter === 'unverified' &&  d.is_verified) return false;
      return true;
    });
  }, [doctors, search, specialtyFilter, verifiedFilter]);

  // Aggregate stats
  const totals = useMemo(() => {
    return doctors.reduce((acc, d) => ({
      referrals:      acc.referrals + (Number(d.referrals_count) || 0),
      conversions:    acc.conversions + (Number(d.converted_count) || 0),
      unpaid:         acc.unpaid + (Number(d.unpaid_incentive_total) || 0),
    }), { referrals: 0, conversions: 0, unpaid: 0 });
  }, [doctors]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Referring Doctors</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} of ${doctors.length} doctors`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setReloadTick(t => t + 1)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={() => navigate('/engage/referring-doctors/new')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm shadow-sm hover:bg-purple-700"
          >
            <Plus className="w-3.5 h-3.5" /> Add Doctor
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-purple-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Total doctors</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{doctors.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Total referrals</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{totals.referrals}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-green-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Converted</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">
            {totals.conversions}
            <span className="text-xs text-gray-500 ml-1">
              ({totals.referrals ? Math.round(totals.conversions / totals.referrals * 100) : 0}%)
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Unpaid incentives</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">₹ {formatINR(totals.unpaid)}</div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, mobile, hospital, reg number…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>
        <select
          value={specialtyFilter}
          onChange={e => setSpecialtyFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All specialties</option>
          {specialties.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={verifiedFilter}
          onChange={e => setVerifiedFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All doctors</option>
          <option value="verified">Verified only</option>
          <option value="unverified">Unverified only</option>
        </select>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            {doctors.length ? 'No doctors match your filters.' : 'No referring doctors yet — click "Add Doctor" to onboard the first one.'}
          </div>
        ) : filtered.map(d => {
          const convPct = d.referrals_count ? Math.round(d.converted_count / d.referrals_count * 100) : 0;
          return (
            <button
              key={d.id}
              onClick={() => navigate(`/engage/referring-doctors/${d.id}`)}
              className="group text-left bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-purple-300 dark:hover:border-purple-700 transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm">
                    {(d.name || '?').replace(/^Dr\.?\s*/i, '').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{d.name}</div>
                    {d.specialty && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{d.specialty}</div>
                    )}
                  </div>
                </div>
                {d.is_verified && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded-full">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>

              {/* Meta */}
              <div className="space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {d.clinic_or_hospital && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Building className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{d.clinic_or_hospital}</span>
                  </div>
                )}
                {d.city && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {d.city}
                  </div>
                )}
                {d.mobile && (
                  <div className="flex items-center gap-1.5">
                    <PhoneIcon className="w-3 h-3" /> {d.mobile}
                  </div>
                )}
              </div>

              {/* Stats row */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{d.referrals_count || 0}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Referrals</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">
                    {convPct}<span className="text-xs">%</span>
                  </div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Conv.</div>
                </div>
                <div>
                  <div className={cn(
                    'text-sm font-bold',
                    Number(d.unpaid_incentive_total) > 0
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-400'
                  )}>
                    ₹{formatINR(d.unpaid_incentive_total)}
                  </div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Owed</div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-[11px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  <Percent className="w-3 h-3" /> {d.referral_incentive_pct || 0}% commission
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-purple-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
